import { useState, useRef } from 'react'
import {
  Plus,
  Pencil,
  Power,
  PowerOff,
  Building2,
  Users,
  BookOpen,
  Globe,
  Clock,
  Link2,
  Upload,
  X,
  Award,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useDeactivateCompany,
  type Company,
  type CreateCompanyDTO,
} from '@/services/super-admin/companies.service'
import { useToast } from '@/hooks/use-toast'
import { LinkUsersDialog } from './LinkUsersDialog'
import { CertificatePreviewDialog } from '@/components/certificate/CertificatePreviewDialog'
import { CertificateBadgePicker } from './companies/CertificateBadgePicker'
import {
  useUploadCompanyLogo,
  useUploadCertificateBackground,
  useUploadCertificateLogo,
  useUploadCertificateSignature,
} from '@/services/upload.service'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getErrorMessage } from '@/utils/error'
import type { SupportChannelType } from '@/types/auth'

export function CompaniesManagement() {
  const { data: companies, isLoading } = useCompanies()
  const createCompany = useCreateCompany()
  const updateCompany = useUpdateCompany()
  const deactivateCompany = useDeactivateCompany()
  const uploadLogo = useUploadCompanyLogo()
  const { toast } = useToast()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Company | null>(null)
  const [deactivating, setDeactivating] = useState<Company | null>(null)
  const [previewing, setPreviewing] = useState<Company | null>(null)

  const [form, setForm] = useState<CreateCompanyDTO>({ name: '', slug: '', domains: [] })
  const [domainInput, setDomainInput] = useState('')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  // Certificate fields (super_admin pode setar tudo direto aqui — usado
  // quando admin da empresa não vai configurar, ou pra setup inicial)
  const bgInputRef = useRef<HTMLInputElement>(null)
  const sigInputRef = useRef<HTMLInputElement>(null)
  const certLogoInputRef = useRef<HTMLInputElement>(null)
  const uploadBg = useUploadCertificateBackground()
  const uploadSig = useUploadCertificateSignature()
  const uploadCertLogo = useUploadCertificateLogo()

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', slug: '', domains: [], certificateBadgeIds: [] })
    setDomainInput('')
    setLogoFile(null)
    setLogoPreview(null)
    setDialogOpen(true)
  }

  const openEdit = (company: Company) => {
    setEditing(company)
    setForm({
      name: company.name,
      slug: company.slug,
      domains: company.domains.map((d) => d.domain),
      certificateBackgroundUrl: company.certificateBackgroundUrl ?? null,
      certificateLogoUrl: company.certificateLogoUrl ?? null,
      certificateSignatureUrl: company.certificateSignatureUrl ?? null,
      directorName: company.directorName ?? null,
      directorRole: company.directorRole ?? null,
      includeGlobalSignature: company.includeGlobalSignature ?? false,
      // Inicializa pelo que veio do back, ordenado por `order` (já vem
      // assim do include do service). Vazio se a empresa nunca configurou.
      certificateBadgeIds:
        company.certificateBadges
          ?.slice()
          .sort((a, b) => a.order - b.order)
          .map((cb) => cb.badge.id) ?? [],
      supportChannelType: company.supportChannelType ?? null,
      supportChannelUrl: company.supportChannelUrl ?? null,
      supportChannelLabel: company.supportChannelLabel ?? null,
    })
    setDomainInput('')
    setLogoFile(null)
    setLogoPreview(company.logo || null)
    setDialogOpen(true)
  }

  const handleCertBgChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const url = await uploadBg.mutateAsync({ file, companyId: editing?.id })
      setForm((prev) => ({ ...prev, certificateBackgroundUrl: url }))
      toast({ title: 'Fundo enviado' })
    } catch (err: unknown) {
      toast({ title: 'Erro', description: getErrorMessage(err), variant: 'destructive' })
    } finally {
      if (bgInputRef.current) bgInputRef.current.value = ''
    }
  }

  const handleCertLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const url = await uploadCertLogo.mutateAsync({ file, companyId: editing?.id })
      setForm((prev) => ({ ...prev, certificateLogoUrl: url }))
      toast({ title: 'Logo do certificado enviada' })
    } catch (err: unknown) {
      toast({ title: 'Erro', description: getErrorMessage(err), variant: 'destructive' })
    } finally {
      if (certLogoInputRef.current) certLogoInputRef.current.value = ''
    }
  }

  const handleCertSigChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const url = await uploadSig.mutateAsync({ file, companyId: editing?.id })
      setForm((prev) => ({ ...prev, certificateSignatureUrl: url }))
      toast({ title: 'Assinatura enviada' })
    } catch (err: unknown) {
      toast({ title: 'Erro', description: getErrorMessage(err), variant: 'destructive' })
    } finally {
      if (sigInputRef.current) sigInputRef.current.value = ''
    }
  }

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const clearLogo = () => {
    setLogoFile(null)
    setLogoPreview(editing?.logo || null)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleSubmit = async () => {
    try {
      let logoUrl = editing?.logo || undefined

      if (logoFile) {
        logoUrl = await uploadLogo.mutateAsync({ file: logoFile, companyId: editing?.id })
      }

      const payload = { ...form, logo: logoUrl }

      if (editing) {
        await updateCompany.mutateAsync({ id: editing.id, ...payload })
        toast({ title: 'Empresa atualizada com sucesso' })
      } else {
        await createCompany.mutateAsync(payload)
        toast({ title: 'Empresa criada com sucesso' })
      }
      setDialogOpen(false)
    } catch (err: unknown) {
      toast({
        title: 'Erro',
        description: getErrorMessage(err, 'Erro ao salvar empresa'),
        variant: 'destructive',
      })
    }
  }

  const handleDeactivate = async () => {
    if (!deactivating) return
    try {
      await deactivateCompany.mutateAsync(deactivating.id)
      toast({ title: `Empresa ${deactivating.name} desativada` })
      setDeactivating(null)
    } catch (err: unknown) {
      toast({
        title: 'Erro',
        description: getErrorMessage(err, 'Erro ao desativar'),
        variant: 'destructive',
      })
    }
  }

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    const slug = !editing
      ? name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
      : form.slug
    setForm((prev) => ({ ...prev, name, slug }))
  }

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase()
    if (!d || (form.domains ?? []).includes(d)) {
      setDomainInput('')
      return
    }
    setForm((prev) => ({ ...prev, domains: [...(prev.domains ?? []), d] }))
    setDomainInput('')
  }

  const removeDomain = (d: string) => {
    setForm((prev) => ({ ...prev, domains: (prev.domains ?? []).filter((x) => x !== d) }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Clock className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Empresas
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {companies?.length || 0} empresa{(companies?.length || 0) !== 1 ? 's' : ''} cadastrada
            {(companies?.length || 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLinkDialogOpen(true)} className="gap-2">
            <Link2 className="w-4 h-4" />
            Vincular Usuários
          </Button>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Empresa
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {companies?.map((company) => (
          <Card key={company.id} className="glass-card border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10 rounded-lg shrink-0">
                    <AvatarImage src={company.logo || undefined} className="object-cover" />
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs">
                      {company.name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {company.name}
                      <Badge variant={company.active ? 'default' : 'secondary'} className="text-xs">
                        {company.active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      {company.domains.length > 0
                        ? company.domains.map((d) => d.domain).join(', ')
                        : 'Sem domínio'}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEdit(company)}
                    title="Editar empresa"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {company.active && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeactivating(company)}
                    >
                      <PowerOff className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {company._count?.users ?? 0} usuários
                </span>
                <span className="flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4" />
                  {company._count?.courses ?? 0} cursos
                </span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-2">slug: {company.slug}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 w-full gap-1.5"
                onClick={() => setPreviewing(company)}
              >
                <Award className="h-3.5 w-3.5" />
                Certificado de exemplo
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* Largura ampla (4xl) pra acomodar a seção de certificado em 2 colunas
            sem precisar de scroll. `max-h-[90vh] overflow-y-auto` mantém
            scroll só como fallback em viewports pequenas. */}
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Atualize os dados da empresa.'
                : 'Preencha os dados para criar uma nova empresa. Usuários com e-mail do domínio serão associados automaticamente.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 rounded-lg">
                  <AvatarImage src={logoPreview || undefined} className="object-cover" />
                  <AvatarFallback className="rounded-lg bg-muted text-muted-foreground">
                    <Building2 className="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    className="gap-1.5"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {logoPreview ? 'Trocar' : 'Enviar'}
                  </Button>
                  {logoFile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearLogo}
                      className="gap-1.5"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="FCamara"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="fcamara"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="domain">Domínios (opcional)</Label>
              <div className="flex gap-2">
                <Input
                  id="domain"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addDomain()
                    }
                  }}
                  placeholder="fcamara.com"
                />
                <Button type="button" variant="outline" onClick={addDomain}>
                  Adicionar
                </Button>
              </div>
              {(form.domains ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(form.domains ?? []).map((d) => (
                    <Badge key={d} variant="secondary" className="gap-1 pr-1">
                      {d}
                      <button
                        type="button"
                        onClick={() => removeDomain(d)}
                        className="rounded-sm hover:text-destructive"
                        aria-label={`Remover ${d}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Usuários com e-mail de qualquer um destes domínios serão vinculados automaticamente.
                Adicione um ou mais.
              </p>
            </div>

            {/* Canal de atendimento — botão flutuante de suporte por empresa */}
            <div className="space-y-3 border-t border-border/40 pt-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Canal de atendimento
              </p>
              <p className="text-xs text-muted-foreground -mt-1">
                Alimenta o botão flutuante de suporte que os usuários desta empresa veem. Deixe como
                &quot;Nenhum&quot; para não exibir o botão.
              </p>

              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={form.supportChannelType ?? 'none'}
                  onValueChange={(v) =>
                    setForm((prev) => ({
                      ...prev,
                      supportChannelType: v === 'none' ? null : (v as SupportChannelType),
                      ...(v === 'none' ? { supportChannelUrl: null } : {}),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.supportChannelType && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="supportChannelUrl">Link do grupo / canal</Label>
                    <Input
                      id="supportChannelUrl"
                      value={form.supportChannelUrl ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          supportChannelUrl: e.target.value || null,
                        }))
                      }
                      placeholder="https://chat.whatsapp.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supportChannelLabel">Texto do card (opcional)</Label>
                    <Input
                      id="supportChannelLabel"
                      value={form.supportChannelLabel ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          supportChannelLabel: e.target.value || null,
                        }))
                      }
                      placeholder="Tire suas dúvidas com a comunidade."
                      maxLength={120}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Certificado — só faz sentido editar quando já tem company criada */}
            {editing && (
              <div className="space-y-3 border-t border-border/40 pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Certificado de conclusão
                </p>

                {/* Linha 1: 2 uploads visuais lado a lado (Fundo + Logo) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fundo do certificado</Label>
                    <div className="flex items-center gap-3">
                      {form.certificateBackgroundUrl ? (
                        <img
                          src={form.certificateBackgroundUrl}
                          alt=""
                          className="w-24 h-16 rounded object-cover border border-border/40"
                        />
                      ) : (
                        <div className="w-24 h-16 rounded bg-muted/40 border border-dashed border-border" />
                      )}
                      <input
                        ref={bgInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleCertBgChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => bgInputRef.current?.click()}
                        disabled={uploadBg.isPending}
                        className="gap-1.5"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {form.certificateBackgroundUrl ? 'Trocar' : 'Enviar'}
                      </Button>
                      {form.certificateBackgroundUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setForm((prev) => ({ ...prev, certificateBackgroundUrl: null }))
                          }
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo do certificado</Label>
                    <div className="flex items-center gap-3">
                      {form.certificateLogoUrl ? (
                        <img
                          src={form.certificateLogoUrl}
                          alt=""
                          className="w-24 h-16 rounded object-contain border border-border/40 bg-white/5"
                        />
                      ) : (
                        <div className="w-24 h-16 rounded bg-muted/40 border border-dashed border-border" />
                      )}
                      <input
                        ref={certLogoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleCertLogoChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => certLogoInputRef.current?.click()}
                        disabled={uploadCertLogo.isPending}
                        className="gap-1.5"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {form.certificateLogoUrl ? 'Trocar' : 'Enviar'}
                      </Button>
                      {form.certificateLogoUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setForm((prev) => ({ ...prev, certificateLogoUrl: null }))}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Logo própria do certificado (separada do logo da empresa). PNG transparente é
                      o ideal — o fundo do certificado é escuro.
                    </p>
                  </div>
                </div>

                {/* Linha 2: representante (nome) + cargo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="directorName">Representante da empresa</Label>
                    <Input
                      id="directorName"
                      value={form.directorName ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          directorName: e.target.value || null,
                        }))
                      }
                      placeholder="Ex.: João da Silva"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="directorRole">Cargo do representante</Label>
                    <Input
                      id="directorRole"
                      value={form.directorRole ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          directorRole: e.target.value || null,
                        }))
                      }
                      placeholder="Ex.: Diretor de Pessoas"
                    />
                  </div>
                </div>

                {/* Linha 3: assinatura + checkbox "incluir assinatura global" */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="space-y-2">
                    <Label>Assinatura</Label>
                    <div className="flex items-center gap-3">
                      {form.certificateSignatureUrl ? (
                        <img
                          src={form.certificateSignatureUrl}
                          alt=""
                          className="w-24 h-10 rounded object-contain border border-border/40 bg-white/5"
                        />
                      ) : (
                        <div className="w-24 h-10 rounded bg-muted/40 border border-dashed border-border" />
                      )}
                      <input
                        ref={sigInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleCertSigChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => sigInputRef.current?.click()}
                        disabled={uploadSig.isPending}
                        className="gap-1.5"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {form.certificateSignatureUrl ? 'Trocar' : 'Enviar'}
                      </Button>
                      {form.certificateSignatureUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setForm((prev) => ({ ...prev, certificateSignatureUrl: null }))
                          }
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
                    <Checkbox
                      checked={form.includeGlobalSignature === true}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          includeGlobalSignature: checked === true,
                        }))
                      }
                      className="mt-0.5"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Incluir assinatura global FCamara</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Adiciona a assinatura do diretor FCamara ao lado da assinatura da empresa no
                        certificado.
                      </span>
                    </span>
                  </label>
                </div>

                {/* Badges no certificado — picker mostra globais + da empresa.
                    Render só na edição (precisa do id da empresa pra trazer
                    as per-company). No create, mantém [] e o super_admin
                    edita depois. */}
                <div className="pt-3 border-t border-border/40 space-y-2">
                  <div>
                    <Label>Badges no certificado</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Selecione as badges que aparecem na faixa central do certificado. Cada usuário
                      verá <strong>somente as que ele conquistou</strong> — as outras são omitidas
                      no PDF dele.
                    </p>
                  </div>
                  <CertificateBadgePicker
                    companyId={editing.id}
                    value={form.certificateBadgeIds ?? []}
                    onChange={(ids) => setForm((prev) => ({ ...prev, certificateBadgeIds: ids }))}
                  />
                </div>
              </div>
            )}
            {!editing && (
              <p className="text-xs text-muted-foreground border-t border-border/40 pt-3">
                Os campos de certificado podem ser configurados depois pelo admin da empresa em{' '}
                <strong>Admin → Empresa</strong>, ou aqui após criar a empresa.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !form.name || !form.slug || createCompany.isPending || updateCompany.isPending
              }
            >
              {createCompany.isPending || updateCompany.isPending
                ? 'Salvando...'
                : editing
                  ? 'Salvar'
                  : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Users Dialog */}
      <LinkUsersDialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen} />

      {/* Certificate Preview */}
      <CertificatePreviewDialog company={previewing} onClose={() => setPreviewing(null)} />

      {/* Deactivate Confirmation */}
      <AlertDialog open={!!deactivating} onOpenChange={(open) => !open && setDeactivating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar {deactivating?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              A empresa será desativada. Novos usuários não serão mais associados automaticamente.
              Usuários existentes não serão afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
